import React, { useEffect, useState } from "react";
import { HelpCircle, Sparkles, Trash2, Save, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import { api } from "../../lib/api";

const SCOPES = [
  { v: "tienda", l: "Tienda" },
  { v: "envio", l: "Envío" },
  { v: "pagos", l: "Pagos" },
  { v: "general", l: "General" },
];

export default function AdminFaq() {
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("tienda");
  const [count, setCount] = useState(8);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/faqs");
      setItems(data || []);
    } catch {
      toast.error("No se pudieron cargar las FAQs");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const generate = async () => {
    setBusy(true);
    const t = toast.loading("IA generando FAQs…");
    try {
      const { data } = await api.post("/admin/ai/faq/generate", {
        scope,
        question_count: count,
      });
      await api.post("/admin/faqs/bulk-import", {
        scope,
        items: data.faqs || [],
      });
      toast.success(`${(data.faqs || []).length} FAQs generadas`, { id: t });
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail === "ai_timeout" ? "IA tardó demasiado" : "IA no disponible",
        { id: t },
      );
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, patchObj) => {
    try {
      await api.patch(`/admin/faqs/${id}`, patchObj);
      load();
    } catch {
      toast.error("Error al guardar");
    }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta FAQ?")) return;
    try {
      await api.delete(`/admin/faqs/${id}`);
      toast.success("Eliminada");
      load();
    } catch {
      toast.error("Error");
    }
  };
  const addBlank = async () => {
    try {
      await api.post("/admin/faqs", {
        scope,
        q: "Nueva pregunta",
        a: "Respuesta",
        order: (items.filter((f) => f.scope === scope).length || 0) + 1,
        published: false,
      });
      load();
    } catch {
      toast.error("No se pudo crear");
    }
  };

  const byScope = SCOPES.reduce((acc, s) => {
    acc[s.v] = items.filter((f) => f.scope === s.v);
    return acc;
  }, {});

  return (
    <div data-testid="admin-faq" className="space-y-4 max-w-5xl">
      <div className="flex items-center gap-2">
        <HelpCircle className="h-5 w-5 text-[#00953B]" />
        <h1 className="text-3xl font-black tracking-tight">FAQ</h1>
      </div>
      <div className="rounded-2xl border bg-white p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label>Ámbito</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="faq-scope" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCOPES.map((s) => (
                <SelectItem key={s.v} value={s.v}>
                  {s.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Nº preguntas</Label>
          <Input
            type="number"
            className="w-24"
            value={count}
            onChange={(e) => setCount(parseInt(e.target.value) || 8)}
            min={3}
            max={20}
            data-testid="faq-count"
          />
        </div>
        <Button
          onClick={generate}
          disabled={busy}
          data-testid="faq-generate-btn"
          variant="outline"
          className="gap-1 rounded-full border-[#00953B] text-[#00953B]"
        >
          <Sparkles className={`h-4 w-4 ${busy ? "animate-pulse" : ""}`} /> Generar con IA
        </Button>
        <Button onClick={addBlank} size="sm" variant="ghost" className="gap-1">
          <Plus className="h-3.5 w-3.5" /> Añadir manual
        </Button>
      </div>
      {SCOPES.map((s) => (
        <section key={s.v} className="space-y-2">
          <h2 className="font-black text-lg">
            {s.l} <span className="text-sm font-normal text-neutral-500">({byScope[s.v]?.length || 0})</span>
          </h2>
          <div className="space-y-2">
            {(byScope[s.v] || []).map((f) => (
              <FaqRow key={f.id} f={f} onPatch={patch} onDelete={del} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function FaqRow({ f, onPatch, onDelete }) {
  const [q, setQ] = useState(f.q);
  const [a, setA] = useState(f.a);
  const dirty = q !== f.q || a !== f.a;
  return (
    <div data-testid={`faq-row-${f.id}`} className="rounded-xl border bg-white p-3 space-y-2">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Pregunta"
        className="font-semibold"
        data-testid={`faq-q-${f.id}`}
      />
      <Textarea
        value={a}
        onChange={(e) => setA(e.target.value)}
        rows={2}
        placeholder="Respuesta"
        data-testid={`faq-a-${f.id}`}
      />
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm">
          <Switch
            checked={f.published}
            onCheckedChange={(v) => onPatch(f.id, { published: v })}
            data-testid={`faq-publish-${f.id}`}
          />
          Publicada
        </label>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            disabled={!dirty}
            onClick={() => onPatch(f.id, { q, a })}
            data-testid={`faq-save-${f.id}`}
          >
            <Save className="h-3 w-3 mr-1" /> Guardar
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(f.id)}
            data-testid={`faq-delete-${f.id}`}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
